import { toast } from '../components/Toast'
import { USER_ROLES } from '../constants/user'
import Papa from 'papaparse'
export const safeStringify = (object) => {
  return JSON.stringify(object)?.replace(/</g, '\\u003c')
}

export const isEqual = (newObj, prevObj) => {
  for (const key in newObj) {
    if (newObj[key] !== prevObj[key]) return false
  }
  return true
}

export const capitalizeMyText = (name, delemiter) => {
  return (name && name?.length > 0 && name.split(delemiter).map(ele =>
    ele.substr(0, 1).toUpperCase().concat(ele.substr(1))
  ).join(' ')) || name
}

export function getMatchingValues (obj1, obj2) {
  const result = {}

  for (const key in obj1) {
    if (obj2?.hasOwnProperty(key)) {
      const arr1 = obj1?.[key]
      const arr2 = obj2?.[key]

      const matchingValues = arr1?.filter(value => arr2?.includes(value))

      if (matchingValues.length > 0) {
        result[key] = matchingValues
      }
    }
  }

  return result
}

export const internationalNumberFormatter = (number = 0) => {
  return new Intl.NumberFormat('en-EU').format(number)
}

export const handleImagePreview = (file, setPreview, route) => {
  if (file) {
    const imageAsBase64 = URL.createObjectURL(file)
    const imageAsFiles = file
    setPreview && setPreview({
      image_preview: imageAsBase64,
      image_file: imageAsFiles
    })
    route && window.open(imageAsBase64, '_blank')
  }
}

export const spreadKeysWithValues = (obj) => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value && typeof value !== 'string') {
      acc[key] = value
    }
    return acc
  }, {})
}

export const removeEmptyData = (obj) => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== '') {
      acc[key] = value
    }
    return acc
  }, {})
}

export const getCountryNames = (countryOptions = [], countryValues) => {
  return countryValues
    .map(value => {
      const match = countryOptions.find(option => option.value === value)
      return match ? match.label : null // Return null if no match is found
    })
    .filter(name => name !== null) // Filter out any null values
    .join(', ') // Concatenate names with a comma and space
}

export const getCountryDetailsByCodes = (countryOptions = [], countryValues) => {
  return countryValues
    .map(value => {
      const match = countryOptions.find(option => option.value === value)
      return match ? { value: match.value, label: match.label } : null // Return object with value and label
    })
    .filter(country => country !== null) // Filter out any null values
}

export const getLabelByValue = (array, value) => {
  const foundItem = array.find(item => item.value === value)
  return foundItem ? foundItem.label : null // Returns null if not found
}

export function areRoutesIncluded (routes) {
  const pathname = window.location.pathname
  const allRoutesExist = routes.some(route => pathname.includes(route))
  return allRoutesExist
}

export const getRoleName = (roleId) => {
  const role = Object.values(USER_ROLES).find(r => r.id === roleId)
  return role ? role.name : 'Admin'
}

/**
 * Downloads the provided data as a CSV file.
 *
 * The data is flattened into rows with columns: Type, Key, and Value.
 * - If the value is an array, each item becomes a separate row.
 * - If the value is an object, each key-value pair becomes a separate row.
 *
 * @param {Object} data - The data to be converted and downloaded as CSV.
 * @param {string} [fileName='downloaded-data.csv'] - The name of the CSV file to download.
 */
export const downloadDataAsCSV = (data, fileName = 'downloaded-data.csv') => {
  const flattened = []

  // Flatten the data into an array of { Type, Key, Value }
  Object.entries(data).forEach(([type, values]) => {
    if (Array.isArray(values)) {
      values.forEach(val => {
        flattened.push({ Type: type, Key: '', Value: val })
      })
    } else if (typeof values === 'object' && values !== null) {
      Object.entries(values).forEach(([k, v]) => {
        flattened.push({ Type: type, Key: k, Value: v })
      })
    }
  })

  // Create CSV string
  const csvContent =
    'data:text/csv;charset=utf-8,' +
    ['Type,Key,Value', ...flattened.map(row => `${row.Type},${row.Key},${row.Value}`)].join('\n')

  // Create and trigger download link
  const encodedUri = encodeURI(csvContent)
  const link = document.createElement('a')
  link.setAttribute('href', encodedUri)
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Uploads and parses a CSV file, then passes the parsed data to a callback.
 *
 * The CSV file must have exactly three columns: Type, Key, and Value.
 * - If Key is empty, the Type is treated as an array of Values.
 * - If Key has a value, the Type is treated as an object with key-value pairs.
 *
 * @param {Event} e - The file input change event containing the selected file.
 * @param {Function} onParsedCallback - The function to call with the parsed and grouped data.
 */
export const uploadDataFromCSV = (e, onParsedCallback) => {
  const file = e.target.files[0]
  if (!file) return

  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    complete: function (results) {
      const rawData = results.data
      const metaFields = results.meta.fields

      // Validate CSV format: must have exactly ['Type', 'Key', 'Value']
      const expectedFields = ['Type', 'Key', 'Value']
      const isValidFormat =
        Array.isArray(metaFields) &&
        expectedFields.length === metaFields.length &&
        expectedFields.every((field, idx) => field === metaFields[idx])

      if (!isValidFormat) {
        toast('Invalid CSV format. Expected headers: Type, Key, Value', 'error')
        return
      }

      const groupedData = {}

      // Group the parsed CSV data by Type
      rawData.forEach(({ Type, Key, Value }) => {
        const type = Type?.trim()
        const key = Key?.trim()
        const value = Value?.trim()

        if (!type) return

        if (!groupedData[type]) {
          groupedData[type] = key ? {} : []
        }

        if (Array.isArray(groupedData[type])) {
          groupedData[type].push(value)
        } else {
          groupedData[type][key] = value
        }
      })

      // Pass parsed data to callback function
      if (typeof onParsedCallback === 'function') {
        onParsedCallback(groupedData)
      }

      // Show success message after successful upload
      toast('CSV uploaded successfully', 'success')
    }
  })
}

/**
 * Formats a numeric balance value to two decimal places and appends the currency code.
 *
 * @param {number|string} value - The balance value to format. Can be a number or a numeric string.
 * @param {string} [currencyCode=''] - Optional currency code to append (e.g., "USD", "INR").
 * @returns {string} A formatted balance string (e.g., "100.00 USD") or "-" if the value is not a valid number.
 *
 * @example
 * formatBalance(123.456, 'USD') // "123.46 USD"
 * formatBalance('98.1', 'EUR')  // "98.10 EUR"
 * formatBalance('abc', 'INR')   // "-"
 * formatBalance(null)           // "-"
 */

export const formatBalance = (value, currencyCode = '') => {
  const number = Number(value)
  if (!isNaN(number)) {
    return `${number.toFixed(2)} ${currencyCode}`.trim()
  }
  return '-'
}
